import { Box } from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { resolveUploadUrl } from '@/utils/format';
import { TYPE } from '@/theme/typography';

/**
 * Tenant brand mark for page headers. Shows the company's uploaded logo
 * (group logo preferred, falling back to company logo) when one exists;
 * otherwise renders a compact initials badge in the brand/primary colour.
 *
 * Reads the active company straight from the auth store so any page can drop
 * it into a header without prop-drilling.
 */
export default function TenantLogo({
  size = 32,
  rounded = 8,
  showFallback = true,
}: { size?: number; rounded?: number; showFallback?: boolean }) {
  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);

  const logoUrl = resolveUploadUrl(
    (activeCompany as any)?.group_logo_url || (activeCompany as any)?.company_logo_url,
  );
  const name =
    (activeCompany as any)?.group_name || activeCompany?.company_name || 'ERP';

  if (logoUrl) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={name}
        sx={{ width: size, height: size, borderRadius: `${rounded}px`, objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }

  // On page headers we don't want a redundant initials badge (the sidebar
  // already shows one) — only render when a real logo is uploaded.
  if (!showFallback) return null;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: `${rounded}px`,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: TYPE.bold,
        fontSize: size * 0.4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
    </Box>
  );
}
